import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Play, Calendar, User, BookOpen, ArrowRight, Headphones, Sparkles } from 'lucide-react'
import ReactPlayer from 'react-player'
import { useCollection } from '../../hooks/useFirestore'
import { sermons as defaultSermons } from '../../data/seedData'
import { formatDate, getYouTubeThumbnail } from '../../utils/helpers'

const LatestSermon = () => {
  const [isPlaying, setIsPlaying] = useState(false)
  const { data: sermons } = useCollection('sermons', {
    orderByField: 'date',
    orderDirection: 'desc',
    limitCount: 1,
  })

  const latestSermon = sermons?.[0] || defaultSermons[0]
  const thumbnail = latestSermon.thumbnailUrl || getYouTubeThumbnail(latestSermon.youtubeUrl)

  return (
    <section className="py-24 bg-gradient-to-b from-white to-light-100 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary-100/50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <Headphones className="w-4 h-4" />
            Fresh Word
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark-100">
            Latest <span className="gradient-text">Sermon</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Video Player */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative group"
          >
            {/* Decorative Frame */}
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 rounded-[2rem] opacity-20 blur-xl group-hover:opacity-40 transition-opacity" />

            <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              {isPlaying ? (
                <ReactPlayer
                  url={latestSermon.youtubeUrl}
                  width="100%"
                  height="100%"
                  playing={true}
                  controls={true}
                />
              ) : (
                <>
                  <img
                    src={thumbnail}
                    alt={latestSermon.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-dark/20 to-transparent" />

                  {/* Play Button */}
                  <motion.button
                    onClick={() => setIsPlaying(true)}
                    className="absolute inset-0 flex items-center justify-center"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-2xl relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full animate-ping opacity-30" />
                      <Play className="w-10 h-10 text-primary-600 ml-1" fill="currentColor" />
                    </div>
                  </motion.button>

                  {/* Duration Badge */}
                  <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-dark/80 backdrop-blur-sm rounded-lg text-white text-sm font-medium">
                    {latestSermon.duration || '45:00'}
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* Sermon Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Badge */}
            <motion.span
              className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 shadow-lg shadow-secondary-500/25"
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles className="w-4 h-4" />
              New Release
            </motion.span>

            <h3 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-4 leading-tight">
              {latestSermon.title}
            </h3>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-light-200 text-dark-300">
                <User className="w-4 h-4 text-primary-500" />
                <span className="text-sm font-medium">{latestSermon.speaker}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-light-200 text-dark-300">
                <Calendar className="w-4 h-4 text-secondary-500" />
                <span className="text-sm font-medium">{formatDate(latestSermon.date)}</span>
              </div>
              {latestSermon.series && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-light-200 text-dark-300">
                  <BookOpen className="w-4 h-4 text-accent-500" />
                  <span className="text-sm font-medium">{latestSermon.series}</span>
                </div>
              )}
            </div>

            <p className="text-dark-300 text-lg mb-6 line-clamp-3 leading-relaxed">
              {latestSermon.description}
            </p>

            {/* Scripture References */}
            {latestSermon.scriptures && (
              <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-5 mb-8 border border-primary-100">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-primary-600" />
                  <span className="text-sm text-primary-600 font-semibold">Scripture References</span>
                </div>
                <p className="text-dark-100 font-medium">{latestSermon.scriptures}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to="/sermons">
                <motion.button
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold shadow-lg shadow-primary-500/25"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Browse All Sermons
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
              {latestSermon.notesUrl && (
                <a href={latestSermon.notesUrl} target="_blank" rel="noopener noreferrer">
                  <motion.button
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-primary-500 text-primary-600 font-semibold hover:bg-primary-50 transition-colors"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Download Notes
                  </motion.button>
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default LatestSermon
